/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  Component, OnDestroy, forwardRef, Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { faPencilIcon } from 'IconLibrary/fontawesome/regular/pencil';
import { isEqual } from 'lodash-es';

import { take } from 'rxjs/operators';
import { SubjectViewModel, PageableUsers } from '../share-panel.d';
import { InviteFormComponent } from '../invite-form/invite-form.component';
import { HttpService } from '../http.service';
import { ShareStateStore, SharePanelOverlays } from '../share-state.store';

@Component({
  selector: 'bulb-modal-select-input',
  templateUrl: './modal-select-input.component.html',
  styleUrls: ['./modal-select-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ModalSelectInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class ModalSelectInputComponent implements ControlValueAccessor, OnDestroy {
  /**
   * The type of Subjects being selected for sharing with.
   */
  @Input() searchSubjectType = 'BULB_USER';

  @Input() parentUnitId: string;

  @Input() placeholder: string;

  @Input() compositeUnit: boolean;

  /**
   * Function to use to fetch the list of potential groups which can be shared with.
   */
  public fetchGroups: () => Observable<PageableUsers>;

  /**
   * Function to use to fetch pages of search results when searching for accounts to share with.
   */
  public searchAccounts: (query: string, page: number) => Observable<PageableUsers>;

  private isModalOpen: boolean = false;

  modalDialog: DynamicDialogRef;

  /**
   * The Subjects that have been selected to be shared with which match the current searchSubjectType.
   */
  selectedSubjects: SubjectViewModel[] = [];

  /**
   * The Subjects that have been selected to be shared with which do not match the current searchSubjectType.
   */
  selectedSubjectsOtherTypes: SubjectViewModel[] = [];

  disabled: boolean = false;

  onTouched: any;

  private lastValue: SubjectViewModel[] = [];

  onChange: Function = () => { };

  constructor(
    private dialogService: DialogService,
    private httpSvc: HttpService,
    private svgRegistry: SvgIconRegistry,
    private modalState: ShareStateStore,
  ) {
    this.searchAccounts = this.httpSvc.searchAccountUsernamesAndEmails.bind(this.httpSvc);
    this.fetchGroups = this.httpSvc.fetchGroupsForSharing.bind(this.httpSvc);
    this.svgRegistry.register([
      faPencilIcon,
    ]);
  }

  writeValue(subjects: SubjectViewModel[]): void {
    const includeTypes = this.searchSubjectType === 'BULB_USER' ? ['BULB_USER'] : ['BULB_GROUP', 'BULB_ORGANIZATION', 'BULB_COL'];
    this.selectedSubjects = subjects.filter((u) => includeTypes.includes(u.permissionType));
    this.selectedSubjectsOtherTypes = subjects.filter((u) => !includeTypes.includes(u.permissionType));

    const oldWithoutInherited = this.lastValue.filter(({ inherited }) => !inherited);
    const newWithoutInherited = subjects.filter(({ inherited }) => !inherited);

    // We only want to emit a change if the non-inherited subjects have changed.
    // The API may add inherited subjects not present in the request.
    // This is a workaround because this form control isn't respecting the
    // { emitEvent: false } option on setValue or patchValue.
    // There is some discussion about this here:
    // https://github.com/angular/angular/issues/20230#issuecomment-818550963
    if (!isEqual(oldWithoutInherited, newWithoutInherited)) {
      this.onChange(subjects);
    }
    this.lastValue = subjects;
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public openDialog(): void {
    this.modalDialog = this.dialogService.open(InviteFormComponent, {
      data: {
        parentUnitId: this.parentUnitId,
        compositeUnit: this.compositeUnit,
        subjectType: this.searchSubjectType,
        fetchSearchPage: this.searchSubjectType === 'BULB_USER' ? this.searchAccounts : this.fetchGroups,
        selectedSubjects: this.selectedSubjects,
      },
      styleClass: 'invite-form-modal',
      width: '352px',
      showHeader: false,
    });

    this.modalState.showPanel(SharePanelOverlays.INVITE_MODAL);

    this.modalDialog.onClose
      .pipe(take(1))
      .subscribe((result: SubjectViewModel[]): void => {
        this.modalState.hidePanel(SharePanelOverlays.INVITE_MODAL);
        if (typeof result === 'boolean') return;
        if (!isEqual(this.selectedSubjects, result)) {
          this.writeValue([...result, ...this.selectedSubjectsOtherTypes]);
        }
      });
  }

  ngOnDestroy() {
    this.modalDialog?.close(false);
  }
}
